import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

export function NotificationBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const openNotif = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); load(); }
    if (n.request_id) navigate(`/requests/${n.request_id}`);
  };

  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-white/10 transition-colors">
          <Bell className="h-5 w-5 text-white" />
          {data.unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Notifikasi</span>
          {data.unread > 0 && (
            <button onClick={markAll} data-testid="mark-all-read-btn" className="text-xs text-brand hover:underline">Tandai semua dibaca</button>
          )}
        </div>
        <ScrollArea className="h-80">
          {data.notifications.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">Belum ada notifikasi</div>
          )}
          {data.notifications.map((n) => (
            <button key={n.id} onClick={() => openNotif(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b hover:bg-muted transition-colors ${!n.read ? "bg-emerald-50/60" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
